import React, { useState, useEffect } from 'react';
import { auth } from '../services/api';
import './Login.css';
import MicrosoftLogo from './MicrosoftLogo';

// Formate "2026-09-07" → "lundi 7 septembre" (le bandeau annonce une date lisible).
function formatDateFr(isoDate) {
  const d = new Date(`${isoDate}T00:00:00`);
  if (isNaN(d)) return null;
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
}

// Jours restants avant la date (0 = aujourd'hui). Négatif = date passée.
function daysUntil(isoDate) {
  const d = new Date(`${isoDate}T00:00:00`);
  if (isNaN(d)) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
}

function Login({ onLogin, authError }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [msLoading, setMsLoading] = useState(false);
  // En coexistence, le formulaire mot de passe est replié derrière un lien :
  // le geste par défaut devient Microsoft dès la semaine de transition.
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  // Modes de connexion proposés par le serveur (Microsoft et/ou mot de passe),
  // et date annoncée de fin du mot de passe (LOGIN_PASSWORD_END_DATE, informative).
  // Par défaut on suppose le mot de passe actif tant que la config n'est pas chargée,
  // pour ne jamais bloquer l'accès si /auth/config échoue.
  const [config, setConfig] = useState({ microsoft: false, password: true, passwordEndDate: null });

  useEffect(() => {
    let mounted = true;
    auth.getConfig()
      .then((res) => { if (mounted) setConfig(res.data); })
      .catch(() => { /* on garde le défaut : formulaire mot de passe affiché */ });
    return () => { mounted = false; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await onLogin(username, password);

    if (!result.success) {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleMicrosoft = async () => {
    setError('');
    setMsLoading(true);
    try {
      const res = await auth.microsoftStart();
      // Redirection du navigateur vers la page de consentement Microsoft.
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.error || 'Connexion Microsoft indisponible');
      setMsLoading(false);
    }
  };

  // Erreur remontée par le retour de redirection Microsoft (/oidc/callback) ou saisie locale.
  const displayError = error || authError;

  // Bandeau d'annonce de la bascule : uniquement en coexistence, avec une date valide.
  const endDate = config.microsoft && config.password ? config.passwordEndDate : null;
  const endDateLabel = endDate ? formatDateFr(endDate) : null;
  const daysLeft = endDate ? daysUntil(endDate) : null;
  const showBanner = Boolean(endDateLabel);

  // Formulaire mot de passe : affiché d'emblée si Microsoft n'est pas proposé,
  // sinon replié derrière un lien discret.
  const passwordVisible = config.password && (!config.microsoft || showPasswordForm);

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-brand">
          {/* L'étiquette d'application (.login-mark-app, pastille « OPS ») est validée
              mais volontairement masquée : elle n'aura de sens qu'à l'arrivée des
              autres produits A97 (BI, RH, FINANCE…). La réactiver = remettre
              <span className="login-mark-app">Ops</span> dans le h1. */}
          <h1 className="login-mark" aria-label="A97">
            A<span>97</span>
          </h1>
          <span className="login-subtitle">Plateforme de gestion</span>
        </div>

        {showBanner && (
          <div className="login-banner" role="status">
            <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16" aria-hidden="true">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4c0 .27.1.52.3.7l2.8 2.8a1 1 0 001.4-1.4L11 9.58V6z" clipRule="evenodd"/>
            </svg>
            <span>
              {daysLeft > 1 && (<>À partir du <strong>{endDateLabel}</strong> (dans {daysLeft} jours), la connexion se fera uniquement avec Microsoft.</>)}
              {daysLeft === 1 && (<>À partir de <strong>demain</strong> ({endDateLabel}), la connexion se fera uniquement avec Microsoft.</>)}
              {daysLeft <= 0 && (<>La connexion par mot de passe est en cours de désactivation : utilisez dès maintenant votre compte Microsoft.</>)}
            </span>
          </div>
        )}

        {displayError && (
          <div className="login-error" role="alert">
            <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
              <path fillRule="evenodd" d="M18 10A8 8 0 11 2 10a8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
            </svg>
            <span>{displayError}</span>
          </div>
        )}

        {config.microsoft && (
          <>
            <button
              type="button"
              className="login-ms-button"
              onClick={handleMicrosoft}
              disabled={msLoading}
            >
              {msLoading ? (
                <>
                  <span className="spinner" />
                  Redirection vers Microsoft…
                </>
              ) : (
                <>
                  <MicrosoftLogo />
                  Se connecter avec Microsoft
                </>
              )}
            </button>
            <p className="login-ms-hint">Utilisez votre adresse e-mail professionnelle.</p>
          </>
        )}

        {config.microsoft && config.password && !passwordVisible && (
          <button
            type="button"
            className="login-password-toggle"
            onClick={() => setShowPasswordForm(true)}
          >
            Se connecter avec un mot de passe
          </button>
        )}

        {config.microsoft && passwordVisible && (
          <div className="login-separator"><span>ou</span></div>
        )}

        {passwordVisible && (
          <form onSubmit={handleSubmit} className="login-form" noValidate>
            <div className="form-field">
              <label htmlFor="username">Identifiant</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Votre identifiant"
                required
                autoComplete="username"
                autoFocus={showPasswordForm}
              />
            </div>

            <div className="form-field">
              <label htmlFor="password">Mot de passe</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                required
                autoComplete="current-password"
              />
            </div>

            <button type="submit" className="login-button" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner" />
                  Connexion en cours…
                </>
              ) : (
                'Se connecter'
              )}
            </button>
          </form>
        )}

        <p className="login-legal">
          Accès réservé au personnel autorisé.
        </p>
      </div>

      <div className="login-bg-overlay" />
    </div>
  );
}

export default Login;
